import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react'
import { Box } from '@mui/material'
import log from 'loglevel'
import { useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { Loading } from '../Loading'
import { UserAuthError } from '../UserAuthError'
import {
  authUser,
  domainName,
  httpRequest,
  updateMail
} from '../../functions/common'
import { isNil } from 'lodash/fp'
import { ServerError } from '../ServerError'
import { AppEntrySubscription } from '@mzc-watchover/terashite-entry-graphql'
import { RegistrationStatus } from '../../types'
import { assign } from 'lodash'

const User = () => {
  const { user } = useAuth0()
  const {
    isLoading: authLoading,
    getAccessTokenSilently,
    loginWithRedirect
  } = useAuth0()
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [memberId, setMemberId] = useState<string | undefined>(undefined)
  const [sub, setSub] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(true)
  const [subscription, setSubscription] = useState<
    AppEntrySubscription | undefined
  >(undefined)
  const [paymentSubscriptions, setPaymentSubscriptions] = useState<
    string[] | undefined
  >(undefined)
  const [hasError, setHasError] = useState<boolean>(false)
  useEffect(() => {
    const checkAuth = async () => {
      if (!isAuthenticated && !authLoading) {
        try {
          await getAccessTokenSilently()
        } catch (error) {
          loginWithRedirect()
        }
      }
    }
    checkAuth()
    const sub = user?.sub as string
    if (!isAuthenticated) {
      authUser(sub)
        .then(
          (
            result:
              | {
                  success: boolean
                  memberId?: string
                }
              | undefined
          ) => {
            if (isNil(result)) {
              log.error('[User] failed auth request')
              return undefined
            }
            if (result.success) {
              setIsAuthenticated(true)
              setMemberId(result.memberId)
              setSub(sub)
            }
          }
        )
        .catch((_error) => {
          setLoading(false)
        })
    }
  }, [isAuthenticated, authLoading, getAccessTokenSilently, loginWithRedirect])

  useEffect(() => {
    const loadCustomerData = async () => {
      if (isNil(memberId) || isNil(sub)) {
        log.error('[CustomerInfo] cannot get customer data')
        setHasError(true)
        return undefined
      }
      const customerData = await httpRequest(
        'get',
        `https://${domainName}/v1/users/${memberId}`,
        undefined,
        { authorization: user?.sub }
      )
      if (isNil(customerData)) {
        log.error('[CustomerInfo] cannot get customer data')
        setHasError(true)
        return undefined
      }
      const { status, data } = customerData
      if (status !== 200) {
        log.error('[CustomerInfo] cannot found customer data')
        setHasError(true)
        return undefined
      }
      const { status: registStatus, subscription, paymentSubscriptions } = data
      if (
        registStatus === RegistrationStatus.subscription &&
        !isNil(paymentSubscriptions)
      ) {
        if (
          !isNil(user) &&
          !isNil(user.email) &&
          subscription.mail !== user.email
        ) {
          const emailUpdate = await updateMail(memberId, user.email, sub)
          if (isNil(emailUpdate) || !emailUpdate) {
            log.error('[CustomerInfo] cannot update email')
          } else {
            assign(subscription, {
              mail: user.email
            })
          }
        }
        setSubscription(subscription)
        setPaymentSubscriptions(paymentSubscriptions)
      } else {
        setHasError(true)
        return undefined
      }
    }
    if (isAuthenticated && isNil(subscription)) {
      loadCustomerData()
        .catch(log.error)
        .finally(() => setLoading(false))
    }
  }, [isAuthenticated])

  return (
    <Box>
      {authLoading || loading ? (
        <Loading />
      ) : !isAuthenticated ? (
        <UserAuthError />
      ) : hasError ? (
        <ServerError />
      ) : (
        <Outlet context={{ memberId, subscription, paymentSubscriptions }} />
      )}
    </Box>
  )
}

export default withAuthenticationRequired(User, {
  onRedirecting: () => <Loading />
})
// export default User
